import React from "react";
import { Home, Wallet, Gift, HandHeart } from "lucide-react";
import { MoneyInput } from "./MoneyInput";
import { AllowanceSlider } from "./AllowanceSlider";
import { Slider } from "./ui/slider";
import { NRB_BASE, RNRB_BASE } from "../lib/iht";

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-1">
    <span className="grid place-items-center w-8 h-8 rounded-lg border-2 border-foreground bg-secondary">
      <Icon size={16} strokeWidth={2.5} />
    </span>
    <h3 className="text-lg font-bold">{children}</h3>
  </div>
);

export const InputForm = ({ v, set }) => (
  <div className="flex flex-col gap-8">
    <div className="flex flex-col gap-4">
      <SectionTitle icon={Wallet}>Your estate</SectionTitle>
      <MoneyInput id="estate" testid="estate-value-input" label="Total estate value" hint="Property, savings, investments & other assets" value={v.estateValue} onChange={(x) => set("estateValue", x)} />
      <MoneyInput id="debts" testid="debts-input" label="Debts & funeral costs" hint="Outstanding debts plus estimated funeral costs" value={v.debtsFuneral} onChange={(x) => set("debtsFuneral", x)} />
    </div>

    <div className="flex flex-col gap-4">
      <SectionTitle icon={Home}>Home & allowances</SectionTitle>
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-bold">Is your main home left to children / grandchildren?</span>
        <div className="grid grid-cols-2 gap-2" data-testid="home-descendants-toggle">
          {[{ v: true, l: "Yes" }, { v: false, l: "No" }].map((o) => {
            const active = v.homeToDescendants === o.v;
            return (
              <button key={o.l} type="button" data-testid={`home-descendants-${o.v}`} onClick={() => set("homeToDescendants", o.v)}
                className={`h-11 rounded-xl border-2 border-foreground font-bold text-sm transition-[transform,box-shadow,background-color] duration-150 ${active ? "bg-foreground text-white shadow-[2px_2px_0px_#1e2b24] translate-x-[1px] translate-y-[1px]" : "bg-white hover:-translate-y-0.5 brutal-shadow-sm"}`}>
                {o.l}
              </button>
            );
          })}
        </div>
      </div>
      {v.homeToDescendants && (
        <MoneyInput id="home" testid="home-value-input" label="Main home value" hint="Optional — helps cap the residence allowance" value={v.homeValue} onChange={(x) => set("homeValue", x)} />
      )}
      <AllowanceSlider testid="spouse-nrb" base={NRB_BASE} label="Unused allowance from late spouse (NRB)" hint="Transferable nil-rate band" value={v.spouseNrbUnused} onChange={(x) => set("spouseNrbUnused", x)} />
      <AllowanceSlider testid="spouse-rnrb" base={RNRB_BASE} label="Unused residence allowance from late spouse (RNRB)" hint="Transferable residence band" value={v.spouseRnrbUnused} onChange={(x) => set("spouseRnrbUnused", x)} />
    </div>

    <div className="flex flex-col gap-4">
      <SectionTitle icon={HandHeart}>Charity</SectionTitle>
      <MoneyInput id="charity" testid="charity-input" label="Amount left to charity" hint="Leaving 10%+ can cut your rate to 36%" value={v.charityLegacy} onChange={(x) => set("charityLegacy", x)} />
    </div>

    <div className="flex flex-col gap-4">
      <SectionTitle icon={Gift}>Gifts in the last 7 years</SectionTitle>
      <MoneyInput id="gifts" testid="gifts-input" label="Total gifts made" hint="Chargeable gifts given away before death" value={v.gifts7y} onChange={(x) => set("gifts7y", x)} />
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold">Years since gifts were made</span>
          <span data-testid="gift-years-value" className="font-mono font-bold px-2.5 py-0.5 rounded-md border-2 border-foreground bg-primary text-sm">
            {v.giftYears} yr{v.giftYears === 1 ? "" : "s"}
          </span>
        </div>
        <Slider data-testid="gift-years-slider" value={[v.giftYears]} min={0} max={7} step={1} onValueChange={(x) => set("giftYears", x[0])} />
        <p className="text-xs text-muted-foreground">Taper relief reduces the tax on gifts after 3 years, reaching zero at 7.</p>
      </div>
    </div>
  </div>
);
